import { func, string } from "prop-types";
import { useContext, useState } from "react";

import styled from "styled-components/macro";

import { BoardContext } from "Boards/BoardContext";
import { useCreateReportBoard } from "Reports/SavedReports/API/queries";

import API from "../../API";
import { ACTION } from "../constants";
import useBoardLocation from "../hooks/useBoardLocation";

import CopyButton from "./CopyButton";
import { useAvailableGroups } from "./useAvailableGroups";

CopyButtonsList.propTypes = {
  selectedAction: string,
  setSelectedAction: func,
  setShow: func,
};

export default function CopyButtonsList({
  selectedAction,
  setSelectedAction,
  setShow,
}) {
  const [{ boardID, groupID }] = useContext(BoardContext);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState();

  const { isReportBoard } = useBoardLocation();

  const { availableGroups, getAvailableGroups } = useAvailableGroups({
    loading,
    setLoading,
    selectedAction,
    setSelectedAction,
    boardID,
  });

  const handleCancel = () => {
    setSelectedAction(null);
  };

  const duplicateBoard = ({
    title,
    groupID,
    copyData = false,
    makeReport,
    share,
  }) => {
    if (loading) return;
    setLoading(true);

    const singleGroupID = Array.isArray(groupID) ? groupID[0] : groupID;
    const groupIDs = Array.isArray(groupID) ? groupID : [groupID];

    API.duplicateBoard({
      sourceBoardID: boardID,
      title,
      destinationGroupID: makeReport ? null : singleGroupID,
      groupIDs: makeReport ? groupIDs : null,
      copyData,
      makeReport,
      share,
    })
      .post()
      .then((resp) => {
        if (resp.boardURL) {
          const newLocation = `/${resp.groupURL}/board/${resp.boardURL}`;
          if (resp.groupURL && resp.boardURL) window.location = newLocation;
        } else if (resp.boardID) {
          const newLocation = `/reports/${resp?.boardID}`;
          window.location = newLocation;
        }
        // setLoading(false);
      })
      .catch((err) => {
        console.log("An error has occurred.");
      });
  };

  const shareColumns = ({ title, groupID, makeReport, share }) => {
    if (loading) return;
    setLoading(true);

    const singleGroupID = groupID[0];
    const groupIDs = Array.isArray(groupID) ? groupID : [groupID];

    API.createNewBoardWithAllColumnsLinked(boardID, {
      title,
      destinationGroupID: makeReport ? null : singleGroupID,
      groupIDs: makeReport ? groupIDs : null,
      makeReport,
      share,
    })
      .post()
      .then((resp) => {
        if (makeReport && resp.newBoardID) {
          const newLocation = `/reports/${resp?.newBoardID}`;
          window.location = newLocation;
        } else if (resp.groupURL && resp.newBoardUrl) {
          const newLocation = `/${resp.groupURL}/board/${resp.newBoardUrl}`;
          window.location = newLocation;
        } else {
          setLoading(false);
          setShow(false);
        }

        // setLoading(false);
      })
      .catch((err) => {
        console.log("An error has occurred.");
      });
  };

  const shareBoard = ({ groupID }) => {
    if (loading) return;
    setLoading(true);
    API.linkBoardToAnotherGroup(boardID, { linkToGroupID: groupID })
      .post()
      .then((resp) => {
        const newLocation = `/${resp.groupURL}/board/${resp.boardURL}`;
        // console.log({ resp, newLocation });
        if (resp.groupURL && resp.boardURL) window.location = newLocation;
        // setLoading(false);
      })
      .catch((err) => {
        console.log("An error has occurred.");
      });
  };

  const { mutate } = useCreateReportBoard({
    onSuccess: (resp) => {
      let newLocation = `/reports/${resp.boardID}`;
      window.fnReactBus.goToUrl(newLocation);
    },
    onError: (err) => {
      console.log("An error has occurred.", err);
    },
  });

  const shouldShowButton = (action) =>
    !selectedAction || selectedAction === action;

  const animationStyles = (action) =>
    selectedAction === action ? {} : { height: 0 };

  const defaultGroupID = groupID === 0 ? [] : [groupID];

  return (
    <Wrapper selectedAction={selectedAction}>
      {/* {shouldShowButton(ACTION.BOARD_STRUCTURE) && ( */}
      <CopyButton
        dataCy="duplicateBoardStructure"
        title="Copy Report Structure"
        text="This will copy the column structure of this report and will not copy any custom column data. Profile field columns and data will be copied."
        iconClass="fal fa-copy"
        selected={selectedAction === ACTION.BOARD_STRUCTURE}
        onClick={() => getAvailableGroups(ACTION.BOARD_STRUCTURE)}
        onSave={duplicateBoard}
        onCancel={handleCancel}
        availableGroups={availableGroups}
        defaultGroupID={defaultGroupID}
        saving={loading}
        shouldShowButton={shouldShowButton(ACTION.BOARD_STRUCTURE)}
        selectedAction={selectedAction}
        delay={0}
        errors={errors}
        setErrors={setErrors}
      />
      {/* )} */}

      {/* {shouldShowButton(ACTION.STRUCTURE_AND_DATA) && ( */}
      <CopyButton
        dataCy="duplicateStructure&Data"
        title="Copy Report Structure & Data"
        text="This will make a copy of all the current data into a new report. Changing custom column data in this current report will not change it in the duplicated report. Profile field column data will be shared between reports."
        iconClass="fad fa-copy"
        selected={selectedAction === ACTION.STRUCTURE_AND_DATA}
        onClick={() => getAvailableGroups(ACTION.STRUCTURE_AND_DATA)}
        onSave={({ title, groupID, makeReport, share }) =>
          duplicateBoard({ title, groupID, copyData: true, makeReport, share })
        }
        onCancel={handleCancel}
        availableGroups={availableGroups}
        defaultGroupID={defaultGroupID}
        saving={loading}
        shouldShowButton={shouldShowButton(ACTION.STRUCTURE_AND_DATA)}
        selectedAction={selectedAction}
        delay={0}
        errors={errors}
        setErrors={setErrors}
      />
      {/* )} */}

      {/* {shouldShowButton(ACTION.SHARE_COLUMNS) && ( */}
      <CopyButton
        dataCy="duplicateShareColumns"
        title="Share the Report Columns"
        text="This will share all the existing columns and data from this report with the new duplicated report. Edits to the data in shared columns will be reflected in the duplicated report."
        iconClass="fal fa-share"
        selected={selectedAction === ACTION.SHARE_COLUMNS}
        onClick={() => getAvailableGroups(ACTION.SHARE_COLUMNS)}
        onSave={shareColumns}
        onCancel={handleCancel}
        availableGroups={availableGroups}
        saving={loading}
        shouldShowButton={shouldShowButton(ACTION.SHARE_COLUMNS)}
        selectedAction={selectedAction}
        delay={0}
        errors={errors}
        setErrors={setErrors}
      />
      {/* )} */}

      {/* {shouldShowButton(ACTION.SHARE_ENTIRE_BOARD) && ( */}
      <CopyButton
        dataCy="duplicateEntireBoard"
        title="Share the Entire Report"
        text="Shared reports are the same across all their groups. Any changes you make to the columns or their data will change across all of their groups."
        iconClass="fad fa-share-all"
        showTitleInput={false}
        selected={selectedAction === ACTION.SHARE_ENTIRE_BOARD}
        onClick={() => getAvailableGroups(ACTION.SHARE_ENTIRE_BOARD)}
        onSave={({ title, groupID }) => shareBoard({ groupID })}
        onCancel={handleCancel}
        availableGroups={availableGroups}
        saving={loading}
        shouldShowButton={shouldShowButton(ACTION.SHARE_ENTIRE_BOARD)}
        selectedAction={selectedAction}
        delay={0}
        errors={errors}
        setErrors={setErrors}
      />
      {/* )} */}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${({ selectedAction }) => `gap: ${selectedAction ? "0" : "12"}px;`}
`;
